import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, UserPermissions, UserContextType } from './UserContext';
import AuthenticatedComponent from '../utilities/AuthenticatedComponent';
import configuration from '../config.js';

// Create the UserContext
export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserContextProviderProps {
  children: ReactNode;
}

const defaultPermissions: UserPermissions = {
  isReadOnly: false,
};

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const [user] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(defaultPermissions);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const authenticatedComponent = new AuthenticatedComponent();

  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(configuration['backend_host'] + '/ahiapi/userPermissions', {
          headers: authenticatedComponent.generateAuthenticatedHeader(),
        });
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        const data = await response.json();
        const readOnly = data?.readOnly || false;
        setPermissions({ isReadOnly: readOnly });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch permissions');
        setPermissions(defaultPermissions);
      } finally {
        setLoading(false);
      }
    };
    fetchPermissions();
  }, []);

  const contextValue: UserContextType = {
    user,
    permissions,
    loading,
    error,
  };

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

// Custom hook for consuming the context
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};
